import type { WorkspacePlugin } from "../plugin.js";

// ── Extension source generator (STATELESS) ──────────────────────────────────
// This function generates the .omp/extensions/skaile.ts file content.
// It is stateless — takes only projectDir, does not require a booted plugin.
// OMP tool set in the generated file is intentionally a reduced subset
// (high-frequency tools only) — the full set is available via the MCP adapter.

/**
 * Generate the `.omp/extensions/skaile.ts` file content for a given project.
 *
 * Stateless — accepts only `projectDir` and embeds it as a JSON string
 * literal in the generated TypeScript source.  No `boot()` or plugin instance
 * is required; this function is safe to call from install commands.
 *
 * The generated file exports `onInit`, `tools`, and `slashCommands` as
 * required by the OMP extension API.
 *
 * @param projectDir - Absolute path to the skaile project root (embedded in
 *   the generated source).
 * @returns A TypeScript source string ready to be written to
 *   `.omp/extensions/skaile.ts`.
 *
 * @docLink packages/workspace-plugin/concepts#adapters
 */
export function buildOmpExtensionSource(projectDir: string): string {
  const escaped = JSON.stringify(projectDir);
  return `// .omp/extensions/skaile.ts
// Generated by: skaile plugin install --driver omp
// or:           skaile repl --driver omp (ephemeral — removed on exit)
// Re-generate:  skaile plugin install --driver omp --project-dir ${projectDir}
import { WorkspacePlugin } from "@skaile/workspaces/workspace-plugin";

const plugin = new WorkspacePlugin({ projectDir: ${escaped}, bootOwned: true });

export async function onInit() {
  await plugin.boot();
  return { systemPromptAppend: plugin.buildSystemPromptSection() };
}

export function tools() {
  return plugin.buildOmpToolSet();
}

export const slashCommands = [
  {
    name: "/flow",
    description: "Flow inspection: /flow list | show <id> | sessions | session <runId>",
    handler: (args: string[]) => plugin.handleSlashCommand("flow", args),
  },
  {
    name: "/connector",
    description: "Connector CRUD: /connector list | read <id> <path> | exec <id> <op> [json-args] | describe <id>",
    handler: (args: string[]) => plugin.handleSlashCommand("connector", args),
  },
  {
    name: "/skill",
    description: "Skill catalog: /skill list [domain] | show <id> | search <query> | run <id>",
    handler: (args: string[]) => plugin.handleSlashCommand("skill", args),
  },
  {
    name: "/mounts",
    description: "Mount management: /mounts list | sync <id> | status <id>",
    handler: (args: string[]) => plugin.handleSlashCommand("mounts", args),
  },
  {
    name: "/validate",
    description: "Validate workspace config (skaile.yaml)",
    handler: () => plugin.handleSlashCommand("validate", []),
  },
];
`;
}

// ── OMP tool set builder ──────────────────────────────────────────────────────
// OMP tool definition shape: { name, description, parameters, handler }
// This is a reduced subset of the MCP tool surface (high-frequency tools only).

/**
 * Build the OMP-native tool set for a booted `WorkspacePlugin`.
 *
 * Returns 13 tool objects in OMP's `{ name, description, parameters, handler }`
 * shape — a high-frequency subset of the 28 MCP tools.  The full tool surface
 * is available via the MCP adapter.
 *
 * Included tools: `mount_list`, `mount_sync`, `flow_list`, `flow_list_sessions`,
 * `skill_list`, `skill_search`, `skill_show`, `connector_list`, `connector_read`,
 * `connector_exec`, `session_list`, `workspace_validate`, `workspace_info`.
 *
 * @param plugin - A booted `WorkspacePlugin` instance.
 * @returns Array of OMP tool definition objects.
 *
 * @docLink packages/workspace-plugin/concepts#adapters
 */
export function buildOmpToolSet(plugin: WorkspacePlugin): unknown[] {
  return [
    {
      name: "mount_list",
      description: "List all mounts declared in skaile.yaml with their mount status.",
      parameters: {},
      handler: async () => {
        const { mountList } = await import("../tools/connectors.js");
        return (await mountList(plugin.connectorManager)).content[0].text;
      },
    },
    {
      name: "mount_sync",
      description: "Sync a mount (pull latest from remote).",
      parameters: { mount_id: { type: "string" } },
      handler: async ({ mount_id }: { mount_id: string }) => {
        const { mountSync } = await import("../tools/connectors.js");
        return (await mountSync(plugin.connectorManager, mount_id)).content[0].text;
      },
    },
    {
      name: "flow_list",
      description: "List all available flow definitions from ai-assets.",
      parameters: {},
      handler: async () => {
        const { flowList } = await import("../tools/flows.js");
        return (await flowList(plugin.projectDir)).content[0].text;
      },
    },
    {
      name: "flow_list_sessions",
      description: "List past flow run sessions for this workspace.",
      parameters: {},
      handler: async () => {
        const { flowListSessions } = await import("../tools/flows.js");
        return (await flowListSessions(plugin.projectDir)).content[0].text;
      },
    },
    {
      name: "skill_list",
      description: "List all skills in the ai-assets catalog.",
      parameters: { domain: { type: "string", optional: true } },
      handler: async ({ domain }: { domain?: string }) => {
        const { skillList } = await import("../tools/skills.js");
        return (await skillList(plugin.projectDir, domain)).content[0].text;
      },
    },
    {
      name: "skill_search",
      description: "Search skills by name, description, or keywords.",
      parameters: { query: { type: "string" } },
      handler: async ({ query }: { query: string }) => {
        const { skillSearch } = await import("../tools/skills.js");
        return (await skillSearch(plugin.projectDir, query)).content[0].text;
      },
    },
    {
      name: "skill_show",
      description: "Show the full SKILL.md content for a skill.",
      parameters: { skill_id: { type: "string" } },
      handler: async ({ skill_id }: { skill_id: string }) => {
        const { skillShow } = await import("../tools/skills.js");
        return (await skillShow(plugin.projectDir, skill_id)).content[0].text;
      },
    },
    {
      name: "connector_list",
      description:
        "List connectors declared in skaile.yaml as {connected, failed}. `failed` holds connectors that did not connect this session, with the failure reason — their mount paths are dead directories, not synced workspaces.",
      parameters: {},
      handler: async () => {
        const { connectorList } = await import("../tools/connectors.js");
        return (await connectorList(plugin.connectorManager)).content[0].text;
      },
    },
    {
      name: "connector_read",
      description: "Read content from a connector by path.",
      parameters: { connector_id: { type: "string" }, path: { type: "string" } },
      handler: async ({ connector_id, path }: { connector_id: string; path: string }) => {
        const { connectorRead } = await import("../tools/connectors.js");
        return (await connectorRead(plugin.connectorManager, connector_id, path)).content[0].text;
      },
    },
    {
      name: "connector_exec",
      description: "Execute a custom operation on a connector.",
      parameters: {
        connector_id: { type: "string" },
        operation: { type: "string" },
        args: { type: "object", optional: true },
      },
      handler: async ({
        connector_id,
        operation,
        args,
      }: {
        connector_id: string;
        operation: string;
        args?: Record<string, unknown>;
      }) => {
        const { connectorExec } = await import("../tools/connectors.js");
        return (await connectorExec(plugin.connectorManager, connector_id, operation, args ?? {}))
          .content[0].text;
      },
    },
    {
      name: "session_list",
      description: "List past agent run sessions.",
      parameters: {},
      handler: async () => {
        const { sessionList } = await import("../tools/sessions.js");
        return (await sessionList(plugin.projectDir)).content[0].text;
      },
    },
    {
      name: "workspace_validate",
      description: "Run a full health check on the workspace config.",
      parameters: {},
      handler: async () => {
        const { workspaceValidate } = await import("../tools/validator.js");
        return (await workspaceValidate(plugin, plugin.driver ?? "omp")).content[0].text;
      },
    },
    {
      name: "workspace_info",
      description: "Show workspace configuration (driver, model, resource counts).",
      parameters: {},
      handler: async () => {
        const { workspaceInfo } = await import("../tools/validator.js");
        return (await workspaceInfo(plugin, plugin.driver ?? "omp", plugin.provider, plugin.model))
          .content[0].text;
      },
    },
  ];
}

// ── Slash command dispatcher ──────────────────────────────────────────────────

/**
 * Dispatch an OMP slash command to the appropriate tool group.
 *
 * Routes the command group string to the corresponding tool functions.
 * Supported groups: `"flow"`, `"connector"`, `"skill"`, `"mounts"`,
 * `"validate"`.  Returns a human-readable string for display in the OMP
 * terminal.
 *
 * Sub-commands per group:
 * - `flow`: `list` | `show <id>` | `sessions` | `session <runId>`
 * - `connector`: `list` | `read <id> <path>` | `exec <id> <op> [json-args]` | `describe <id>`
 * - `skill`: `list [domain]` | `show <id>` | `search <query>` | `run <id>`
 * - `mounts`: `list` | `sync <id>` | `status <id>`
 * - `validate`: (no sub-commands)
 *
 * @param plugin - A booted `WorkspacePlugin` instance.
 * @param group  - Slash command group name (without the leading `/`).
 * @param args   - Tokenised arguments following the group name.
 * @returns Plain-text result string for the OMP terminal.
 *
 * @docLink packages/workspace-plugin/concepts#adapters
 */
export async function handleSlashCommand(
  plugin: WorkspacePlugin,
  group: string,
  args: string[],
): Promise<string> {
  const sub = args[0];
  const rest = args.slice(1);

  try {
    switch (group) {
      case "flow":
        return await handleFlowCommand(plugin, sub, rest);
      case "connector":
        return await handleConnectorCommand(plugin, sub, rest);
      case "skill":
        return await handleSkillCommand(plugin, sub, rest);
      case "mounts":
        return await handleMountsCommand(plugin, sub, rest);
      case "validate":
        return await handleValidateCommand(plugin);
      default:
        return `Unknown group '${group}'`;
    }
  } catch (e: unknown) {
    return `Error: ${e instanceof Error ? e.message : String(e)}`;
  }
}

async function handleFlowCommand(
  plugin: WorkspacePlugin,
  sub: string | undefined,
  rest: string[],
): Promise<string> {
  const { flowList, flowShow, flowListSessions, flowGetSession } = await import(
    "../tools/flows.js"
  );
  if (!sub || sub === "list") return (await flowList(plugin.projectDir)).content[0].text;
  if (sub === "show" && rest[0])
    return (await flowShow(plugin.projectDir, rest[0])).content[0].text;
  if (sub === "sessions") return (await flowListSessions(plugin.projectDir)).content[0].text;
  if (sub === "session" && rest[0])
    return (await flowGetSession(plugin.projectDir, rest[0])).content[0].text;
  return "Usage: /flow list | show <id> | sessions | session <runId>";
}

async function handleConnectorCommand(
  plugin: WorkspacePlugin,
  sub: string | undefined,
  rest: string[],
): Promise<string> {
  const { connectorList, connectorRead, connectorExec, connectorDescribe } = await import(
    "../tools/connectors.js"
  );
  if (!sub || sub === "list") return (await connectorList(plugin.connectorManager)).content[0].text;
  if (sub === "read" && rest[0] && rest[1])
    return (await connectorRead(plugin.connectorManager, rest[0], rest[1])).content[0].text;
  if (sub === "exec" && rest[0] && rest[1]) {
    let args: Record<string, unknown> = {};
    if (rest[2]) {
      try {
        args = JSON.parse(rest.slice(2).join(" "));
      } catch {
        /* best-effort parse */
      }
    }
    return (await connectorExec(plugin.connectorManager, rest[0], rest[1], args)).content[0].text;
  }
  if (sub === "describe" && rest[0])
    return (await connectorDescribe(plugin.connectorManager, rest[0])).content[0].text;
  return "Usage: /connector list | read <id> <path> | exec <id> <op> [json-args] | describe <id>";
}

async function handleSkillCommand(
  plugin: WorkspacePlugin,
  sub: string | undefined,
  rest: string[],
): Promise<string> {
  const { skillList, skillShow, skillSearch } = await import("../tools/skills.js");
  if (sub === "run" && rest[0])
    return (await skillShow(plugin.projectDir, rest[0])).content[0].text;
  if (!sub || sub === "list") return (await skillList(plugin.projectDir, rest[0])).content[0].text;
  if (sub === "show" && rest[0])
    return (await skillShow(plugin.projectDir, rest[0])).content[0].text;
  if (sub === "search" && rest[0])
    return (await skillSearch(plugin.projectDir, rest.join(" "))).content[0].text;
  return "Usage: /skill list [domain] | show <id> | search <query> | run <id>";
}

async function handleMountsCommand(
  plugin: WorkspacePlugin,
  sub: string | undefined,
  rest: string[],
): Promise<string> {
  const { mountList, mountSync, mountStatus } = await import("../tools/connectors.js");
  if (!sub || sub === "list") return (await mountList(plugin.connectorManager)).content[0].text;
  if (sub === "sync" && rest[0])
    return (await mountSync(plugin.connectorManager, rest[0])).content[0].text;
  if (sub === "status" && rest[0])
    return (await mountStatus(plugin.connectorManager, rest[0])).content[0].text;
  return "Usage: /mounts list | sync <id> | status <id>";
}

async function handleValidateCommand(plugin: WorkspacePlugin): Promise<string> {
  const { workspaceValidate } = await import("../tools/validator.js");
  return (await workspaceValidate(plugin, plugin.driver ?? "omp")).content[0].text;
}
